import type {ReactElement} from 'react';
import {SocialPlatform} from '@Components/social-media/social-media.types';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {isInstagramAccount, isTikTokAccount, isYoutubeAccount} from '@Components/social-media/account.vo';
import type {MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import type {PreviewMediaOptions} from './social-post-content-media-wrapper/social-post-content-media-wrapper.types';
import {GoogleBusinessProfileCtaType, PlatformMediaType} from '@Components/social-media/post.vo';

export enum SocialPostContentType {
  DEFAULT = 'DEFAULT', // default is in the dialog default
  MEDIA_RESIZING = 'MEDIA_RESIZING', // resizing
  MEDIA_RESIZING_FAILURE = 'MEDIA_RESIZE_FAILURE', // failure
  ORPHANED_PREVIEW = 'ORPHANED_PREVIEW', // accounts page
  ACCOUNT_PREVIEW = 'ACCOUNT_PREVIEW', // this is when u click an account in the dialog
}

export enum PreviewKind {
  VERTICAL_VIDEO = 'VERTICAL_VIDEO',
  STORY = 'STORY',
  FEED_POST = 'FEED_POST',
}

export const getPreviewKind = (account: SocialAccount | undefined, platformMediaType: PlatformMediaType): PreviewKind => {
  if (isReelShownAsVerticalPreview(account, platformMediaType) || isYoutubeAccount(account) || isTikTokAccount(account)) {
    return PreviewKind.VERTICAL_VIDEO;
  }

  if (platformMediaType === PlatformMediaType.STORIES) {
    return PreviewKind.STORY;
  }

  return PreviewKind.FEED_POST;
};

const isReelShownAsVerticalPreview = (account: SocialAccount | undefined, platformMediaType: PlatformMediaType): boolean => {
  return platformMediaType === PlatformMediaType.REELS && !isInstagramAccount(account);
};

export interface GraphicsChangeDependencies {
  accountId: SocialMediaAccountId | undefined;
  platformMediaType: PlatformMediaType;
  graphicsSignature: string;
}

/** Horizontal room kept around the preview card inside its parent before the card starts scaling down. */
export const DEFAULT_PREVIEW_WIDTH_INSET_PX = 32;

/** Intrinsic width of the account-less design preview card (Figma "account step preview" post). */
export const ORPHANED_PREVIEW_WIDTH_PX = 387;
export const ORPHANED_PREVIEW_HEIGHT_PX = 444;

export interface PlatformPostPreviewProps {
  account: SocialAccount;
  platformMediaType: PlatformMediaType;
  publishingParams: Record<SocialMediaAccountId, MediaTypePublishingParams>;
  numberOfPages: number;
  renderMedia: (options: PreviewMediaOptions) => ReactElement;
}

export interface ReelDimensions {
  width: string;
  height: string;
}

export interface PostDimensions {
  width: string;
  height: string;
}

export const POST_DIMENSIONS_BY_PLATFORM: Record<SocialPlatform, PostDimensions> = {
  [SocialPlatform.FACEBOOK]: {width: '305px', height: '339px'},
  [SocialPlatform.FACEBOOK_GROUP]: {width: '305px', height: '339px'},
  [SocialPlatform.FACEBOOK_PAGE]: {width: '305px', height: '339px'},
  [SocialPlatform.FACEBOOK_PROFILE]: {width: '305px', height: '339px'},
  [SocialPlatform.TWITTER]: {width: '303px', height: '342px'},
  [SocialPlatform.TIKTOK]: {width: '305px', height: '354px'},
  [SocialPlatform.INSTAGRAM]: {width: '305px', height: '339px'},
  [SocialPlatform.LINKEDIN]: {width: '305px', height: '344px'},
  [SocialPlatform.LINKEDIN_PAGE]: {width: '305px', height: '344px'},
  [SocialPlatform.LINKEDIN_PROFILE]: {width: '305px', height: '344px'},
  [SocialPlatform.YOUTUBE]: {width: '305px', height: '354px'},
  [SocialPlatform.PINTEREST]: {width: '310px', height: '349px'},
  [SocialPlatform.GOOGLE_BUSINESS_PROFILE]: {width: '305px', height: '320px'},
  [SocialPlatform.THREADS]: {width: '303px', height: '338px'},
};

export const STORY_DIMENSIONS: PostDimensions = {
  width: '170px',
  height: '342px',
};

export const REEL_DIMENSIONS_BY_PLATFORM: Record<SocialPlatform, ReelDimensions> = {
  [SocialPlatform.FACEBOOK]: {width: '170px', height: '338px'},
  [SocialPlatform.FACEBOOK_GROUP]: {width: '170px', height: '338px'},
  [SocialPlatform.FACEBOOK_PAGE]: {width: '170px', height: '338px'},
  [SocialPlatform.FACEBOOK_PROFILE]: {width: '170px', height: '338px'},
  [SocialPlatform.TWITTER]: {width: '170px', height: '338px'},
  [SocialPlatform.TIKTOK]: {width: '170px', height: '338px'},
  [SocialPlatform.INSTAGRAM]: {width: '170px', height: '338px'},
  [SocialPlatform.LINKEDIN]: {width: '170px', height: '338px'},
  [SocialPlatform.LINKEDIN_PAGE]: {width: '170px', height: '338px'},
  [SocialPlatform.LINKEDIN_PROFILE]: {width: '170px', height: '338px'},
  [SocialPlatform.YOUTUBE]: {width: '170px', height: '338px'},
  [SocialPlatform.PINTEREST]: {width: '170px', height: '338px'},
  [SocialPlatform.GOOGLE_BUSINESS_PROFILE]: {width: '170px', height: '338px'},
  [SocialPlatform.THREADS]: {width: '170px', height: '338px'},
};

export const getGoogleBusinessProfileCtaText = (ctaType: GoogleBusinessProfileCtaType): string => {
  const ctaTextByType: Record<GoogleBusinessProfileCtaType, string> = {
    [GoogleBusinessProfileCtaType.BOOK]: window.i18next.t('pmwjs_google_business_profile_cta_book'),
    [GoogleBusinessProfileCtaType.ORDER]: window.i18next.t('pmwjs_google_business_profile_cta_order'),
    [GoogleBusinessProfileCtaType.SHOP]: window.i18next.t('pmwjs_google_business_profile_cta_shop'),
    [GoogleBusinessProfileCtaType.LEARN_MORE]: window.i18next.t('pmwjs_google_business_profile_cta_learn'),
    [GoogleBusinessProfileCtaType.SIGN_UP]: window.i18next.t('pmwjs_google_business_profile_cta_signup'),
    [GoogleBusinessProfileCtaType.CALL]: window.i18next.t('pmwjs_google_business_profile_cta_call'),
  };

  return ctaTextByType[ctaType];
};

export const getPreviewStyleConfig = (accountType: SocialPlatform, isReel: boolean, platformMediaType: PlatformMediaType): ReelDimensions | PostDimensions => {
  if (platformMediaType === PlatformMediaType.STORIES) {
    return STORY_DIMENSIONS;
  }

  return isReel ? REEL_DIMENSIONS_BY_PLATFORM[accountType] : POST_DIMENSIONS_BY_PLATFORM[accountType];
};
